import { useState, useCallback, useRef } from 'react'
import { message } from 'antd'

export const usePharmacyManagement = ({ apiClasses, onPharmacyChange }) => {
    const [showChangePharmacyModal, setShowChangePharmacyModal] = useState(false)
    const [pharmacySearchResults, setPharmacySearchResults] = useState([])
    const [pharmacySpecialtyTypes, setPharmacySpecialtyTypes] = useState({})
    const [pharmacySearchLoading, setPharmacySearchLoading] = useState(false)
    
    const abortController = useRef(null)

    const searchPharmacies = useCallback(async (params) => {
        if (!apiClasses?.PrescriptionsAPI) {
            console.warn('PrescriptionsAPI not available in apiClasses')
            return
        }

        setPharmacySearchLoading(true)
        setPharmacySearchResults([])

        if (abortController.current) {
            abortController.current.abort()
        }
        abortController.current = new AbortController()

        try {
            const response = await apiClasses.PrescriptionsAPI.searchPharmacies(params, abortController.current.signal)
            
            if (abortController.current.signal.aborted) {
                return
            }
            
            let pharmacies = []
            if (response.data.pharmacies) {
                pharmacies = response.data.pharmacies
            } else if (response.data.pharmacy) {
                pharmacies = [response.data.pharmacy]
            } else if (Array.isArray(response.data)) {
                pharmacies = response.data
            }
            
            setPharmacySearchResults(pharmacies || [])
        } catch (error) {
            if (error.name === 'AbortError' || abortController.current?.signal.aborted) {
                return
            }
            
            console.error('Error searching pharmacies:', error)
            message.error('Error searching pharmacies')
        } finally {
            setPharmacySearchLoading(false)
        }
    }, [apiClasses])

    const getPharmacySpecialtyTypes = useCallback(async () => {
        if (!apiClasses?.PrescriptionsAPI) {
            console.warn('PrescriptionsAPI not available in apiClasses')
            return
        }

        try {
            const { data: { pharmacies } } = await apiClasses.PrescriptionsAPI.getPharmacySpecialtyTypes()
            setPharmacySpecialtyTypes(pharmacies || {})
        } catch (error) {
            console.error('Error fetching pharmacy specialty types:', error)
        }
    }, [apiClasses])

    const selectPharmacy = useCallback((pharmacyName) => {
        const selectedPharmacy = pharmacySearchResults.find(pharmacy => pharmacy.name === pharmacyName)
        if (selectedPharmacy && onPharmacyChange) {
            onPharmacyChange(selectedPharmacy)
        }
        setPharmacySearchResults([])
        setShowChangePharmacyModal(false)
    }, [pharmacySearchResults, onPharmacyChange])

    const handleCancelSearch = useCallback(() => {
        setPharmacySearchResults([])
        setShowChangePharmacyModal(false)
    }, [])

    const showChangePharmacyModalHandler = useCallback(() => {
        setShowChangePharmacyModal(true)
        getPharmacySpecialtyTypes()
    }, [getPharmacySpecialtyTypes])

    return {
        showChangePharmacyModal,
        pharmacySearchResults,
        pharmacySpecialtyTypes,
        pharmacySearchLoading,
        searchPharmacies,
        selectPharmacy,
        handleCancelSearch,
        showChangePharmacyModalHandler
    }
}
